import { ReactElement, ReactNode } from 'react';
import { confirmable, ConfirmDialogProps, createConfirmation } from 'react-confirm';

import PublicCollectionConfirmationContent from '@/collection/components/PublicCollectionConfirmationContent/PublicCollectionConfirmationContent';
import { DRIVE_ROOT_NAME } from '@/drive/constants/driveRootName';

import { PRIMARY, TERTIARY } from '../components/Button/Button';
import Confirmation, { ConfirmationOptions } from '../components/inputs/Confirmation/Confirmation';

const defaultConfirmation = createConfirmation(Confirmation);

export function confirm(confirmation: ReactNode, options: ConfirmationOptions = {}) {
  return defaultConfirmation({ confirmation, ...options });
}

export default class ConfirmationFactory {
  // This will generate confirmation function without duplication of code within the project
  //
  // @example
  // export function myModalConfirmation({ someProp }: SomePropsInterface) {
  //   return ConfirmationFactory.generate(({ show, proceed, dismiss }: ConfirmDialogProps<unknown, boolean>) => (
  //     <MyModal
  //       isShowed={show}
  //       onSelect={(value: Any) => proceed(value)}
  //       onClose={dismiss}
  //       someProp={someProp}
  //     />
  //   ));
  // }
  static generate<Response>(callback: (props: ConfirmDialogProps<unknown, Response>) => ReactElement) {
    const confirmation = createConfirmation(confirmable<unknown, Response>(callback));

    return confirmation({});
  }

  static confirm(body: ReactNode, options: ConfirmationOptions = {}) {
    return confirm(body, options);
  }

  static makeDeleteConfirmation(entityName: string, isPlural = false) {
    return confirm(
      `This action cannot be undone. This will permanently delete the ${entityName}${isPlural ? 's' : ''}.`,
      {
        title: `Delete ${isPlural ? 'these' : 'this'} ${entityName}${isPlural ? 's' : ''}?`,
        okLabel: 'Delete',
        okColor: TERTIARY,
      },
    );
  }

  static makePublicCollectionConfirmation() {
    return confirm(<PublicCollectionConfirmationContent />, {
      title: 'Some products are hidden',
      okLabel: 'Publish Anyway',
      okColor: PRIMARY,
    });
  }

  static makeB2BIdChangeConfirmation() {
    return confirm('Are you going to change b2bSid?', {
      title: 'Are you sure?',
    });
  }

  static makeSKUChangeConfirmation() {
    return confirm(
      'SKU was changed. Any previously shared link to this product will not be available after applying changes.',
      {
        title: 'Sensitive data changing confirmation',
      },
    );
  }

  static makeMediaSourceDeleteConfirmation() {
    return confirm('This action cannot be undone. This will permanently delete the media source.', {
      title: 'Remove this media source file?',
      okLabel: 'Delete',
      okColor: TERTIARY,
    });
  }

  static makeDropChangesConfirmation() {
    return confirm('Your changes will be dropped', {
      title: 'Sensitive data changing confirmation',
    });
  }

  static makeChangeCutShapeConfirmation() {
    return confirm('You have an open full access to the cut model data', {
      title: 'Attention!',
      showCancelButton: false,
    });
  }

  static makeProjectRenderingConfirmation(productCount: number) {
    return confirm(`Do you want to render ${productCount} product(s)?`, {
      title: 'Are you sure?',
    });
  }

  static makeDeleteDriveProjectConfirmation(title: string) {
    return confirm(`This action cannot be undone. This will permanently delete ${title}`, {
      title: 'Are you sure?',
      okLabel: 'Delete',
      okColor: TERTIARY,
    });
  }

  static makeDeleteFromDriveProjectConfirmation(title: string) {
    return confirm(`This action cannot be undone. This will permanently delete ${title} from multistage project`, {
      title: 'Are you sure?',
      okLabel: 'Delete',
      okColor: TERTIARY,
    });
  }

  static makeDeleteDriveItemConfirmation(folderTitle: Nullable<string>) {
    const location = folderTitle ? `'${folderTitle}'` : DRIVE_ROOT_NAME;

    return confirm(`Do you want to delete item from ${location}?`, {
      title: 'Are you sure?',
      okLabel: 'Delete',
      isCloseHidden: true,
    });
  }

  static makeSelectSolutionConfirmation(solutionId: number) {
    return confirm(
      <div>
        <p>Only one Gold Star may be set at time!</p>

        <p>
          <span>To set Gold Star to this plan, first remove it from plan </span>

          <span>&#34;{solutionId}&#34;</span>
        </p>
      </div>,
      {
        title: 'Set Gold Star mark',
        okColor: PRIMARY,
        showCancelButton: false,
      },
    );
  }

  static makeRemoveMilestoneConfirmation() {
    return confirm('This action cannot be undone. This will permanently delete milestone from drive-project', {
      title: 'Are you sure?',
      okLabel: 'Delete',
      okColor: TERTIARY,
    });
  }

  static makeCancelTaskConfirmation() {
    return confirm('The task will be stopped. This cannot be undone.', {
      title: 'Terminate task?',
      okLabel: 'Terminate task',
      okColor: TERTIARY,
      cancelLabel: 'Keep task running',
    });
  }

  static makeUnfollowCollectionConfirmation() {
    return confirm('You will stop following this collection and it will be removed from your Collections.', {
      title: 'Unfollow this collection?',
      okLabel: 'Unfollow',
      okColor: TERTIARY,
    });
  }

  static makeDeleteContactConfirmation() {
    return confirm('This action cannot be undone. This will permanently delete the contact.', {
      title: 'Are you sure?',
      okLabel: 'Delete',
      okColor: TERTIARY,
    });
  }
}
